import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { User, FileText, FileClock, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ATTENDANCE_STATUS, HOMEWORK_STATUS, HOMEWORK_ASSIGNED_BADGE, VIDEO_STATUS } from '../../utils/constants';

// Translation key mappings for status labels
const ATTENDANCE_TRANSLATION_KEYS = {
  1: { label: 'status.present', desc: 'status.presentDesc' },
  2: { label: 'status.late', desc: 'status.lateDesc' },
  3: { label: 'status.canceled', desc: 'status.canceledDesc' },
  4: { label: 'status.absent', desc: 'status.absentDesc' },
  5: { label: 'status.late', desc: 'status.lateDesc' },
};

const HOMEWORK_TRANSLATION_KEYS = {
  'on-time': { label: 'status.hwOnTime', desc: 'status.hwOnTimeDesc' },
  'On-Time': { label: 'status.hwOnTime', desc: 'status.hwOnTimeDesc' },
  'ON-TIME': { label: 'status.hwOnTime', desc: 'status.hwOnTimeDesc' },
  'ontime': { label: 'status.hwOnTime', desc: 'status.hwOnTimeDesc' },
  'late': { label: 'status.hwLate', desc: 'status.hwLateDesc' },
  'Late': { label: 'status.hwLate', desc: 'status.hwLateDesc' },
  'LATE': { label: 'status.hwLate', desc: 'status.hwLateDesc' },
  'no-submission': { label: 'status.hwMissing', desc: 'status.hwMissingDesc' },
  'No-Submission': { label: 'status.hwMissing', desc: 'status.hwMissingDesc' },
  'NO-SUBMISSION': { label: 'status.hwMissing', desc: 'status.hwMissingDesc' },
  'missing': { label: 'status.hwMissing', desc: 'status.hwMissingDesc' },
  'Missing': { label: 'status.hwMissing', desc: 'status.hwMissingDesc' },
};

const VIDEO_TRANSLATION_KEYS = {
  true: { label: 'status.watched', desc: 'status.watchedDesc' },
  false: { label: 'status.notWatched', desc: 'status.notWatchedDesc' },
  1: { label: 'status.watched', desc: 'status.watchedDesc' },
  0: { label: 'status.notWatched', desc: 'status.notWatchedDesc' },
};

// Global state to track which tooltip is open (ensures only one at a time)
let activeTooltipId = null;
let activeTooltipClose = null;

export function Tooltip({ children, text }) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, arrowOffset: 0 });
  const triggerRef = useRef(null);
  const tooltipId = useId();
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

  const closeTooltip = useCallback(() => {
    setIsVisible(false);
    if (activeTooltipId === tooltipId) {
      activeTooltipId = null;
      activeTooltipClose = null;
    }
  }, [tooltipId]);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = 32; // Approximate tooltip height
      const tooltipWidth = 200; // Max tooltip width estimate
      const padding = 8;

      let top = rect.top - padding;
      const triggerCenter = rect.left + rect.width / 2;
      let left = triggerCenter;
      let arrowOffset = 0; // Offset from center in pixels

      // Ensure tooltip stays within horizontal bounds
      const halfWidth = tooltipWidth / 2;
      if (left - halfWidth < padding) {
        const newLeft = halfWidth + padding;
        arrowOffset = triggerCenter - newLeft;
        left = newLeft;
      } else if (left + halfWidth > window.innerWidth - padding) {
        const newLeft = window.innerWidth - halfWidth - padding;
        arrowOffset = triggerCenter - newLeft;
        left = newLeft;
      }

      // If tooltip would go above screen, show below instead
      if (top - tooltipHeight < padding) {
        top = rect.bottom + padding + tooltipHeight;
      }

      setPosition({ top, left, arrowOffset });
    }
  }, []);

  const handleMouseEnter = () => {
    if (!isMobile) {
      updatePosition();
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsVisible(false);
    }
  };

  const handleClick = (e) => {
    if (isMobile) {
      e.stopPropagation();

      // Close any other open tooltip first
      if (activeTooltipId && activeTooltipId !== tooltipId && activeTooltipClose) {
        activeTooltipClose();
      }

      if (!isVisible) {
        updatePosition();
        setIsVisible(true);
        activeTooltipId = tooltipId;
        activeTooltipClose = closeTooltip;
      } else {
        closeTooltip();
      }
    }
  };

  // Close on outside click, scroll, or touch move (drag) for mobile
  useEffect(() => {
    if (isMobile && isVisible) {
      const handleOutsideClick = (e) => {
        if (triggerRef.current && !triggerRef.current.contains(e.target)) {
          closeTooltip();
        }
      };
      const handleScroll = () => closeTooltip();
      const handleTouchMove = () => closeTooltip();

      document.addEventListener('click', handleOutsideClick);
      document.addEventListener('scroll', handleScroll, true);
      document.addEventListener('touchmove', handleTouchMove, { passive: true });

      return () => {
        document.removeEventListener('click', handleOutsideClick);
        document.removeEventListener('scroll', handleScroll, true);
        document.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isMobile, isVisible, closeTooltip]);

  // Cleanup on unmount
  useEffect(() => {
    const currentId = tooltipId;
    return () => {
      if (activeTooltipId === currentId) {
        activeTooltipId = null;
        activeTooltipClose = null;
      }
    };
  }, [tooltipId]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className="inline-flex"
      >
        {children}
      </div>
      {isVisible && createPortal(
        <div
          className="fixed z-[9999] px-2 py-1 bg-gray-800 text-white text-xs rounded max-w-[200px] text-center pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{ top: position.top, left: position.left }}
        >
          {text}
          <div
            className="absolute top-full -translate-x-1/2 border-4 border-transparent border-t-gray-800"
            style={{ left: `calc(50% + ${position.arrowOffset}px)` }}
          />
        </div>,
        document.body
      )}
    </>
  );
}

export function AttendanceIndicator({ status, iconOnly = false }) {
  const { t } = useTranslation();

  if (status === null || status === undefined) return null;

  const config = ATTENDANCE_STATUS[status];
  const translationKeys = ATTENDANCE_TRANSLATION_KEYS[status];
  if (!config || !translationKeys) return null;

  const label = t(translationKeys.label);
  const description = t(translationKeys.desc);

  if (iconOnly) {
    return (
      <Tooltip text={`${label}: ${description}`}>
        <span className={`inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full ${config.color}`}>
          <User className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip text={description}>
      <span className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium ${config.color}`}>
        <User className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        {label}
      </span>
    </Tooltip>
  );
}

export function HomeworkIndicator({ status, iconOnly = false }) {
  const { t } = useTranslation();

  if (status === null || status === undefined) return null;

  const config = HOMEWORK_STATUS[status];
  const translationKeys = HOMEWORK_TRANSLATION_KEYS[status];
  if (!config || !translationKeys) return null;

  const label = t(translationKeys.label);
  const description = t(translationKeys.desc);

  if (iconOnly) {
    return (
      <Tooltip text={`${label}: ${description}`}>
        <span className={`inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full ${config.color}`}>
          <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip text={description}>
      <span className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium ${config.color}`}>
        <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        {label}
      </span>
    </Tooltip>
  );
}

// Separate badge that shows when homework is assigned (regardless of submission status)
export function HomeworkAssignedIndicator({ hasHomework, iconOnly = false }) {
  const { t } = useTranslation();

  if (!hasHomework) return null;

  const config = HOMEWORK_ASSIGNED_BADGE;
  const label = t('status.hwAssigned');
  const description = t('status.hwAssignedDesc');

  if (iconOnly) {
    return (
      <Tooltip text={`${label}: ${description}`}>
        <span className={`inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full ${config.color}`}>
          <FileClock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip text={description}>
      <span className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium ${config.color}`}>
        <FileClock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        {label}
      </span>
    </Tooltip>
  );
}

export function VideoIndicator({ watched, iconOnly = false }) {
  const { t } = useTranslation();

  if (watched === null || watched === undefined) return null;

  const config = VIDEO_STATUS[watched];
  const translationKeys = VIDEO_TRANSLATION_KEYS[watched];
  if (!config || !translationKeys) return null;

  const label = t(translationKeys.label);
  const description = t(translationKeys.desc);

  if (iconOnly) {
    return (
      <Tooltip text={`${label}: ${description}`}>
        <span className={`inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full ${config.color}`}>
          <PlayCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip text={description}>
      <span className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium ${config.color}`}>
        <PlayCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        {label}
      </span>
    </Tooltip>
  );
}
